<!-- PedidoPaso3.vue — Subir PDFs de proyectos de diseño (opcional) -->
<template>
  <div>
    <!-- Zona de carga -->
    <div
      class="upload-zone mb-4"
      :class="{ dragging: arrastrando }"
      @click="$refs.inputProyectos.click()"
      @dragover.prevent="arrastrando = true"
      @dragleave.prevent="arrastrando = false"
      @drop.prevent="handleDrop"
    >
      <input
        ref="inputProyectos"
        type="file"
        accept="application/pdf"
        multiple
        class="d-none"
        @change="handleChange"
      />
      <div class="upload-inner">
        <i class="bi bi-file-earmark-plus upload-icon"></i>
        <div>
          <p class="fw-bold text-cafe mb-0">Arrastra los PDFs de proyecto aquí</p>
          <small class="text-muted"
            >o haz clic para seleccionar · puedes subir varios a la vez</small
          >
        </div>
      </div>
    </div>

    <!-- Lista vacía -->
    <div v-if="proyectos.length === 0" class="vacio-container">
      <i class="bi bi-file-earmark-pdf"></i>
      <p class="mt-2 mb-0">Sin proyectos adjuntos</p>
      <small>Este paso es opcional — puedes guardar el pedido sin proyectos</small>
    </div>

    <!-- Lista de proyectos -->
    <div v-else class="proyectos-lista">
      <small class="text-cafe fw-bold d-block mb-2">
        {{ proyectos.length }} archivo(s) listo(s) para subir
      </small>

      <div v-for="(p, idx) in proyectos" :key="idx" class="proyecto-card">
        <div class="proyecto-header">
          <div class="proyecto-meta">
            <i class="bi bi-file-earmark-pdf-fill me-2 text-danger"></i>
            <span class="proyecto-nombre">{{ p.archivo.name }}</span>
            <span class="proyecto-size text-muted ms-2"
              >({{ formatSize(p.archivo.size) }})</span
            >
          </div>
          <div class="d-flex align-items-center gap-2">
            <button
              class="btn-toggle-preview"
              @click="p._preview = !p._preview"
              :title="p._preview ? 'Ocultar preview' : 'Ver PDF'"
            >
              <i class="bi" :class="p._preview ? 'bi-eye-slash' : 'bi-eye'"></i>
              {{ p._preview ? "Ocultar" : "Ver" }}
            </button>
            <button class="btn-quitar" @click="quitarProyecto(idx)" title="Quitar">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <!-- Preview embebido -->
        <div v-if="p._preview && p._objectUrl" class="preview-container">
          <embed :src="p._objectUrl" type="application/pdf" class="pdf-embed" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";

const props = defineProps({
  proyectos: { type: Array, required: true },
});
const emit = defineEmits(["update:proyectos"]);

const arrastrando = ref(false);

function handleChange(e) {
  agregarArchivos([...e.target.files]);
  e.target.value = "";
}

function handleDrop(e) {
  arrastrando.value = false;
  const archivos = [...e.dataTransfer.files].filter((f) => f.type === "application/pdf");
  agregarArchivos(archivos);
}

function agregarArchivos(archivos) {
  const existentes = props.proyectos.map((p) => p.archivo.name);
  const nuevos = archivos
    .filter((f) => !existentes.includes(f.name))
    .map((archivo) => ({
      archivo,
      _preview: false,
      _objectUrl: URL.createObjectURL(archivo),
    }));
  emit("update:proyectos", [...props.proyectos, ...nuevos]);
}

function quitarProyecto(idx) {
  const lista = [...props.proyectos];
  if (lista[idx]._objectUrl) URL.revokeObjectURL(lista[idx]._objectUrl);
  lista.splice(idx, 1);
  emit("update:proyectos", lista);
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}

.upload-zone {
  border: 2px dashed var(--d-green);
  border-radius: 12px;
  background: rgba(96, 108, 56, 0.05);
  padding: 0.85rem 1.25rem;
  cursor: pointer;
  transition: 0.2s;
}
.upload-zone:hover,
.upload-zone.dragging {
  border-color: var(--d-green-dark);
  background: rgba(40, 54, 24, 0.08);
}
.upload-inner {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}
.upload-icon {
  font-size: 1.7rem;
  color: var(--d-green);
  flex-shrink: 0;
}

.vacio-container {
  text-align: center;
  padding: 1.75rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}
.vacio-container i {
  font-size: 2.4rem;
  display: block;
}
.vacio-container small {
  font-weight: 400;
  font-size: 0.8rem;
}

.proyectos-lista {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.proyecto-card {
  border: 1px solid rgba(96, 108, 56, 0.3);
  border-radius: 10px;
  background: white;
  overflow: hidden;
}
.proyecto-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.75rem;
  background: rgba(96, 108, 56, 0.06);
  border-bottom: 1px solid rgba(96, 108, 56, 0.12);
}
.proyecto-meta {
  display: flex;
  align-items: center;
  min-width: 0;
}
.proyecto-nombre {
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--d-green-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 340px;
}
.proyecto-size {
  font-size: 0.75rem;
  flex-shrink: 0;
}

.btn-toggle-preview {
  background: none;
  border: 1px solid var(--d-green);
  color: var(--d-green);
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 6px;
  padding: 2px 9px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: 0.15s;
}
.btn-toggle-preview:hover {
  background: var(--d-green);
  color: var(--d-cream);
}

.btn-quitar {
  background: none;
  border: none;
  color: #c0392b;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  transition: background 0.15s;
  line-height: 1;
}
.btn-quitar:hover {
  background: rgba(192, 57, 43, 0.1);
}

.preview-container {
  border-top: 1px solid rgba(96, 108, 56, 0.15);
  background: #f5f5f5;
}
.pdf-embed {
  width: 100%;
  height: 480px;
  display: block;
  border: none;
}
</style>
